import { useMemo } from 'react';

import { useIsAuthenticated } from '@domains/pdp-common';

import { GetPdpSizeInputPage_pdp_size_input_page_size_category_list_children as PdpSizeTemplateChild } from '../graphql/pdp-size-input-page_types.2';
import { usePdpSizeTemplateQuery } from '../queries/usePdpSizeTemplateQuery';
import { useGetUserAccountProductMeasurementListQuery } from '../services/useSizeInfoMutation';

interface Props {
  sizeCategoryId?: string;
  headers?: string[];
}

/**
 * 유저 실측 데이터(My Size)를 조회하고
 * 템플릿 정보를 기반으로 테이블 행(Row) 데이터를 생성하는 훅
 */
export const useUserMeasureRow = ({ sizeCategoryId, headers }: Props) => {
  const { isLoggedIn } = useIsAuthenticated();

  // 1. 유저 실측 정보 조회 (리스트 조회 - 동의 여부 및 전체 데이터 확인용)
  const {
    data: userMeasurementData,
    refetch: refetchUserMeasurement,
    isLoading: isUserMeasurementLoading,
  } = useGetUserAccountProductMeasurementListQuery({
    enabled: isLoggedIn,
  });

  const userMeasurementList = userMeasurementData?.data?.user_account_product_measurement_list?.item_list;

  // 동의 여부 판단: is_size_info_agreed 필드로 확인
  const isUserSizeAgreed = useMemo(() => {
    if (!isLoggedIn) return false;
    return userMeasurementData?.data?.user_account_product_measurement_list?.is_size_info_agreed ?? false;
  }, [isLoggedIn, userMeasurementData]);

  // 2. 사이즈 템플릿 정보 조회
  const { data: templateData } = usePdpSizeTemplateQuery(sizeCategoryId, {
    enabled: !!sizeCategoryId,
  });

  // 3. 현재 카테고리에 맞는 유저 실측 데이터 매칭
  const matchedUserMeasurement = useMemo(() => {
    if (!userMeasurementList || !sizeCategoryId) {
      return null;
    }

    const sid = String(sizeCategoryId);

    // ✅ 1순위: secondary_category_id로 매칭 (가디건=6 같은 하위 카테고리 명시적 매칭)
    const bySecondary = userMeasurementList.find(
      (item: { secondary_category_id?: string }) => String(item.secondary_category_id ?? '') === sid,
    );
    if (bySecondary) return bySecondary;

    // ✅ 2순위: (fallback) category_id로 매칭 (혹시 서비스가 상위 카테고리 기준으로만 저장된 경우)
    const byCategory = userMeasurementList.find((item: { category_id: string }) => String(item.category_id) === sid);
    return byCategory;
  }, [userMeasurementList, sizeCategoryId]);

  // 4. 동적 매핑을 통해 테이블 행 데이터 생성
  const userMeasureRow: string[] | null = useMemo(() => {
    if (!matchedUserMeasurement || !headers || !templateData || !sizeCategoryId) return null;

    // 템플릿 찾기
    const categoryList = templateData.pdp_size_input_page?.size_category_list || [];
    let targetTemplate: PdpSizeTemplateChild | undefined;

    for (const category of categoryList) {
      const found = category.children.find((child) => child.id === sizeCategoryId);
      if (found) {
        targetTemplate = found;
        break;
      }
    }

    if (!targetTemplate) return null;

    // 매핑 맵 생성
    const nameToKeyMap: Record<string, string> = {};
    targetTemplate.template_value.forEach((tv) => {
      if (tv.name) {
        nameToKeyMap[tv.name] = tv.key;
      }
    });

    // 값 매핑
    const values = headers.slice(1).map((headerName) => {
      const key = nameToKeyMap[headerName];
      if (!key) return '-';

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const val = (matchedUserMeasurement.measurement as any)[key];
      return val !== null && val !== undefined ? String(val) : '-';
    });

    return ['내 사이즈', ...values];
  }, [matchedUserMeasurement, headers, templateData, sizeCategoryId]);

  return {
    userMeasureRow,
    refetchUserMeasurement,
    isUserSizeAgreed,
    /** 유저 실측 데이터 로딩 중 여부 */
    isLoading: isUserMeasurementLoading,
  };
};
